import { config } from 'config';
import { generalApi, GeneralApiTagTypes } from 'core/api';

import {
    TAttachmentFileResponseBody,
    TAttachmentResponseBody,
    TGetAttachmentFileRequestBody,
    TGetAttachmentRequestBody,
    TGetAttachmentUploadLinkRequestBody,
    TGetAttachmentUploadLinkResponseBody,
    TListAttachmentsRequestBody,
    TListAttachmentsResponseBody,
} from '../types';

export const attachmentsApi = generalApi.injectEndpoints({
    endpoints: (builder) => ({
        listAttachments: builder.query<TListAttachmentsResponseBody, TListAttachmentsRequestBody>({
            query: ({
                commissionId,
                includeCommission,
                includeInvoice,
                includeUploadedBy,
                invoiceId,
                withCommissionsDeliveryNotes,
            }) => {
                const params = new URLSearchParams();

                if (includeUploadedBy) params.append('include[]', 'uploadedBy');
                if (includeCommission) params.append('include[]', 'commission');
                if (includeInvoice) params.append('include[]', 'invoice');
                if (commissionId) params.append('commissionId', commissionId.toString());
                if (invoiceId) params.append('invoiceId', invoiceId.toString());
                if (withCommissionsDeliveryNotes) params.append('withCommissionsDeliveryNotes', 'true');

                return {
                    url: config.api.endpoints.attachments.list,
                    method: 'GET',
                    params,
                };
            },
            providesTags: (_, __, arg) => [
                { type: GeneralApiTagTypes.attachment.list, id: arg.commissionId || arg.invoiceId },
            ],
        }),
        getAttachment: builder.query<TAttachmentResponseBody, TGetAttachmentRequestBody>({
            query: ({
                attachmentId,
                includeUploadedBy,
                includeCommission,
                includeInvoice,
                includeInvoiceEmailData,
            }) => {
                const params = new URLSearchParams();

                if (includeUploadedBy) params.append('include[]', 'uploadedBy');
                if (includeCommission) params.append('include[]', 'commission');
                if (includeInvoice) params.append('include[]', 'invoice');
                if (includeInvoiceEmailData) params.append('include[]', 'invoiceEmailData');
                return {
                    url: config.api.endpoints.attachments.get.replace(':id', attachmentId.toString()),
                    method: 'GET',
                    params,
                };
            },
            providesTags: (_, __, arg) => [{ type: GeneralApiTagTypes.attachment.item, id: JSON.stringify(arg) }],
        }),
        getAttachmentFile: builder.query<TAttachmentFileResponseBody, TGetAttachmentFileRequestBody>({
            query: ({ attachmentId, download }) => {
                return {
                    url: config.api.endpoints.attachments.getFile.replace(':id', attachmentId.toString()),
                    method: 'GET',
                    params: {
                        download,
                    },
                    responseHandler: async (response) => response,
                };
            },
            transformResponse: async (response: Response) => {
                const data = URL.createObjectURL(await response.blob());
                const headers = response.headers;
                return { data, headers };
            },
            providesTags: (_res, _error, args) => [
                ...(args.download ? [] : [{ type: GeneralApiTagTypes.attachment.file, id: args.attachmentId }]),
            ],
        }),
        checkUploadToken: builder.query<void, void>({
            query: () => {
                return {
                    url: config.api.endpoints.attachments.checkUploadToken,
                    method: 'GET',
                    responseHandler: 'text',
                };
            },
        }),
        getAttachmentUploadLink: builder.query<
            TGetAttachmentUploadLinkResponseBody,
            TGetAttachmentUploadLinkRequestBody
        >({
            query: ({ commissionId, renew }) => {
                return {
                    url: config.api.endpoints.attachments.generateUploadLink.replace(':id', commissionId.toString()),
                    method: 'GET',
                    params: {
                        renew,
                    },
                };
            },
            providesTags: (_, __, arg) => [{ type: GeneralApiTagTypes.attachment.uploadLink, id: JSON.stringify(arg) }],
        }),
    }),
});

export const {
    useGetAttachmentQuery,
    useLazyGetAttachmentQuery,
    useGetAttachmentFileQuery,
    useLazyGetAttachmentFileQuery,
    useListAttachmentsQuery,
    useLazyListAttachmentsQuery,
    useCheckUploadTokenQuery,
    useLazyCheckUploadTokenQuery,
    useGetAttachmentUploadLinkQuery,
    useLazyGetAttachmentUploadLinkQuery,
} = attachmentsApi;
